import React, { useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { SummaryCards } from '../../components/supportRequests/SummaryCards/SummaryCards';
import { RequestsToolbar } from '../../components/supportRequests/RequestsToolbar/RequestsToolbar';
import { RequestsTable } from '../../components/supportRequests/RequestsTable/RequestsTable';
import { RequestModal } from '../../components/supportRequests/RequestModal/RequestModal';
import { ExportRequestsModal } from '../../components/supportRequests/ExportRequestsModal/ExportRequestsModal';
import { useSupportRequests } from '../../hooks/useSupportRequests';
import { useUpdateSupportRequestMutation } from '../../entities/supportRequests/api/supportRequestApi';
import { formatRangeLabel } from '../../utils/format';
import styles from './SupportRequestsPage.module.scss';

export function SupportRequestsPage() {
    const toast = useToast();
    const [openId, setOpenId] = useState<string | null>(null);
    const [exportOpen, setExportOpen] = useState(false);

    const {
        requests,
        rows,
        total,
        counts,
        summary,
        filters,
        hasFilters,
        patchFilters,
        setStatusTab,
        resetFilters,
        sort,
        toggleSort,
        page,
        pageCount,
        setPage,
        perPage,
        isLoading,
        isError,
        refetch,
    } = useSupportRequests();

    const [updateRequest] = useUpdateSupportRequestMutation();

    const openRequest = openId ? (requests.find((r) => r.id === openId) ?? null) : null;

    const handleSend = async (id: string, text: string) => {
        if (!text) {
            toast('Введите текст ответа');
            return;
        }
        const request = requests.find((r) => r.id === id);
        try {
            await updateRequest({
                id,
                patch: { answer: text, ...(request?.status === 'new' ? { status: 'work' as const } : {}) },
            }).unwrap();
            toast('Ответ отправлен на ' + (request?.email ?? ''));
            setOpenId(null);
        } catch {
            toast('Не удалось отправить ответ');
        }
    };

    const handleComplete = async (id: string) => {
        try {
            await updateRequest({ id, patch: { status: 'done' } }).unwrap();
            toast('Запрос завершён');
            setOpenId(null);
        } catch {
            toast('Не удалось завершить запрос');
        }
    };

    const emptyState = (() => {
        if (isError) {
            return (
                <EmptyState
                    title="Не удалось загрузить обращения"
                    description="Проверьте соединение и попробуйте ещё раз."
                    action={{ label: 'Повторить', onClick: refetch }}
                />
            );
        }

        if (rows.length === 0) {
            return (
                <EmptyState
                    title="Под фильтры ничего не подошло"
                    description="Измените запрос или сбросьте фильтры — покажем все обращения."
                    action={hasFilters ? { label: 'Сбросить фильтры', onClick: resetFilters } : undefined}
                />
            );
        }

        return undefined;
    })();

    return (
        <div className={styles.page}>
            <PageHeader
                title="Ответы на запросы пользователей"
                actions={<Button onClick={() => setExportOpen(true)}>Выгрузить отчёт</Button>}
            />

            <SummaryCards summary={summary} />

            <RequestsToolbar
                filters={filters}
                counts={counts}
                hasFilters={hasFilters}
                onQueryChange={(query) => patchFilters({ query })}
                onThemeChange={(theme) => patchFilters({ theme })}
                onPriorityChange={(priority) => patchFilters({ priority })}
                onStatusChange={setStatusTab}
                onReset={resetFilters}
            />

            <section className={styles.pane}>
                <RequestsTable
                    rows={rows}
                    sort={sort}
                    scrollKey={`${page}-${sort.key}-${sort.dir}-${JSON.stringify(filters)}`}
                    isLoading={isLoading}
                    loadingText="Загружаем обращения…"
                    onSort={toggleSort}
                    onOpen={setOpenId}
                    footer={
                        total > 0 ? (
                            <TableFooter
                                page={page}
                                totalPages={pageCount}
                                rangeLabel={formatRangeLabel(page, perPage, total)}
                                onPageChange={setPage}
                            />
                        ) : undefined
                    }
                    empty={emptyState}
                />
            </section>

            <RequestModal
                request={openRequest}
                onClose={() => setOpenId(null)}
                onSend={handleSend}
                onComplete={handleComplete}
            />

            <ExportRequestsModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                onNotify={toast}
            />
        </div>
    );
}